import { Box, Grid, styled, Theme, Typography } from '@mui/material'

import { convertToComponent } from '@ecm/data/Contentful'
import { HubSpotResponse } from '@ecm/data/HubSpot'
import { Lead } from '@ecm/data/Lead'
import { Partner } from '@ecm/data/Partner'
import HowItWorksBlocks from '@ecm/ui/component/block/HowItWorks'
import GenericLeadForm from '@ecm/ui/component/form/GenericLeadForm'
import CloudflareImage from '@ecm/ui/element/CloudflareImage'
import Color from '@ecm/ui/element/Color'
import MainContainer from '@ecm/ui/element/MainContainer'
import Testimonial from '@ecm/ui/element/Testimonial'

import mapWithLinesIllustration from '/public/images/illustration/map-with-lines.svg'
import rocketShipIllustration from '/public/images/illustration/rocket-ship.svg'
import stackOfMailIllustration from '/public/images/illustration/stack-of-mail.svg'

export type PartnersPageProps = {
  className?: string
  hasDiscount: boolean | 0 | undefined
  partner: Partner
  onFailure: (message: string) => () => void
  onSuccess: (lead: Lead, response: HubSpotResponse) => () => void
  title: JSX.Element | null
}

const PartnersPage = styled(
  ({ className, hasDiscount, partner, onFailure, onSuccess, title }: PartnersPageProps) => {
    return (
      <div className={className}>
        <Box bgcolor={Color.smoke.normal}>
          <MainContainer sx={{ paddingY: '64px' }}>
            <Grid
              display="grid"
              gap="2em"
              alignItems="center"
              gridTemplateColumns={{ md: '1fr 1fr' }}
              justifyItems={{ md: 'end', xs: 'center' }}
            >
              <Grid
                container
                direction="column"
                justifyContent="space-between"
                alignItems={{ md: 'start', xs: 'center' }}
                textAlign={{ md: 'left', xs: 'center' }}
              >
                <Typography variant="h2" component="h1">
                  {partner.heading1}
                </Typography>
                {partner.heroDescriptionRich ? (
                  <>
                    {convertToComponent(partner.heroDescriptionRich)}
                    {partner.slug === 'stripe-atlas' && (
                      <Typography variant="p4">
                        *Based on survey of small business owners commissioned by LegalZoom (2023)
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="p2">{partner.heroDescription}</Typography>
                )}
              </Grid>

              <GenericLeadForm
                buttonWidth="100%"
                green
                formId={partner.hubSpotFormId}
                maxWidth="30em"
                onFailure={onFailure}
                onSuccess={onSuccess}
                submitText={hasDiscount ? 'Redeem Offer' : 'Choose a plan today'}
                title={title}
              />
            </Grid>
          </MainContainer>
        </Box>

        <HowItWorksBlocks.Partnership
          header="Take your postal mail from your mailbox to your inbox"
          subHeading="Benefits"
        />

        <MainContainer>
          {partner.stackOfMailHeader && partner.stackOfMailBody && (
            <Grid
              display="grid"
              justifyItems="center"
              alignItems="start"
              paddingY="64px"
              gridTemplateColumns={{
                md: '1fr 1fr',
                sm: '1.3fr 0.7fr ',
                xs: '1fr',
              }}
            >
              <Grid className="partner-body" container direction="column">
                <Typography variant="h4" component="h2">
                  {partner.stackOfMailHeader}
                </Typography>
                {convertToComponent(partner.stackOfMailBody)}
              </Grid>

              <CloudflareImage src={stackOfMailIllustration} />
            </Grid>
          )}

          {partner.mapPinHeader && partner.mapPinBody && (
            <Grid
              display="grid"
              columnGap="2em"
              justifyItems="center"
              alignItems="start"
              paddingY="64px"
              gridTemplateColumns={{
                md: '1fr 1fr',
                sm: '0.7fr 1.3fr',
                xs: '1fr',
              }}
            >
              <CloudflareImage src={mapWithLinesIllustration} />

              <Grid className="partner-body" container direction="column">
                <Typography variant="h4" component="h2" padding="1em 0 0 0">
                  {partner.mapPinHeader}
                </Typography>
                {convertToComponent(partner.mapPinBody)}
              </Grid>
            </Grid>
          )}

          {partner.rocketShipHeader && partner.rocketShipBody && (
            <Grid
              display="grid"
              justifyItems="center"
              alignItems="start"
              paddingY="64px"
              gridTemplateColumns={{
                md: '1fr 1fr',
                sm: '1.3fr 0.7fr',
                xs: '1fr',
              }}
            >
              <Grid className="partner-body" container direction="column">
                <Typography variant="h4" component="h2">
                  {partner.rocketShipHeader}
                </Typography>
                {convertToComponent(partner.rocketShipBody)}
              </Grid>

              <CloudflareImage src={rocketShipIllustration} />
            </Grid>
          )}
        </MainContainer>

        {partner.testimonials.length && (
          <Box padding="16px">
            <Testimonial testimonials={partner.testimonials} />
          </Box>
        )}

        <Box paddingY="64px" bgcolor={Color.saint.normal}>
          <MainContainer>
            <Grid
              display="grid"
              gap="2em"
              alignItems="center"
              gridTemplateColumns={{ md: '1fr 1fr' }}
              justifyItems={{ md: 'end', xs: 'center' }}
              bgcolor={Color.saint.normal}
            >
              <Box textAlign={{ md: 'left', xs: 'center' }}>
                <CloudflareImage
                  src={`/images/logo/${partner.slug}-partner-white.svg`}
                  width={360}
                  height={40}
                />

                <Typography variant="h2" component="h2" color={Color.white.normal} my="0.5em">
                  {hasDiscount ? (
                    <> Get {partner.discountPercent}% off today </>
                  ) : (
                    <> Connect Earth Class Mail and {partner.name} today </>
                  )}
                </Typography>
              </Box>

              <GenericLeadForm
                buttonWidth="100%"
                green
                formId={partner.hubSpotFormId}
                maxWidth="30em"
                onFailure={onFailure}
                onSuccess={onSuccess}
                submitText={hasDiscount ? 'Redeem Offer' : 'Choose a plan today'}
              />
            </Grid>
          </MainContainer>
        </Box>
      </div>
    )
  }
)(({ theme }: { theme: Theme }) => ({
  '.partner-body': {
    [theme.breakpoints.down('sm')]: {
      order: 2,
    },
  },
}))

export default PartnersPage
